import {
  Grid3x3,
  ListChecks,
  Network,
  Palmtree,
  Star,
  Target,
  UserCog,
  UserSearch,
  Users,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** HRM is HR-facing — mirrors the HRM nav gate. */
const HR_ROLES: Role[] = ['super_admin', 'founder', 'hr'];

export const HRM_GUIDES: Guide[] = [
  {
    slug: 'hrm-overview',
    title: 'Managing your people',
    module: 'hrm',
    summary: 'The HR side: hire, onboard, develop and review your team.',
    icon: UserCog,
    roles: HR_ROLES,
    body: `**People (HRM)** is the HR-facing side of the workspace — where you manage
the team's full lifecycle. (The *employee's own* view of all this lives in
**My Space**.)

\`\`\`
 RECRUIT ──▶  HIRE ──▶  ONBOARD ──▶  DEVELOP ──▶  REVIEW
 (openings,   (employee  (checklist)  (skills,     (performance,
  candidates)  record)                 dev plan)    feedback)
\`\`\`

### What's here
- **Recruitment** — openings, candidates, interviews and offers.
- **Employees** — the directory and each person's full record.
- **Time Off, Onboarding, Performance Reviews, PDP, Skills Matrix, Org Chart** —
  the day-to-day of running a team.

### Who owns what
HR manages people; **compensation is owned by Finance** — salary and payslip
figures sit with the finance team. Recruitment **offer** amounts are part of
HR's remit and stay with HR.`,
  },
  {
    slug: 'hrm-recruitment',
    title: 'Recruitment',
    module: 'hrm',
    summary: 'Openings → candidates → interviews → offers, in one flow.',
    icon: UserSearch,
    roles: HR_ROLES,
    body: `**Recruitment** runs hiring end to end.

\`\`\`
 OPENING ──▶ CANDIDATE ──▶ INTERVIEW ──▶ OFFER ──▶ HIRED
\`\`\`

### The pieces
- **Openings** — the roles you're hiring for (**Open → On hold → Closed → Filled**);
  add an external link to where the role is also posted.
- **Candidates** — people in the funnel (**New → Screening → Interviewing → Offer
  → Hired / Rejected**).
- **Interviews** — schedule them and record a **scorecard** (rate skills 1–5:
  English, problem solving, soft skills, etc.); outcome is **Pending → Passed / Failed**.
- **Offers** — the formal offer (**Draft → Sent → Accepted / Declined**), with the
  amount and an attached document. Offer amounts are visible to HR.

### How to use it
Post an opening, add candidates and move them along, capture interview scorecards
so decisions are evidence-based, then send an offer. Accepting an offer leads into
creating the employee.

### Why it's here
A single pipeline keeps hiring organised and fair — every candidate, conversation
and decision in one auditable place.`,
  },
  {
    slug: 'hrm-employees',
    title: 'Employees',
    module: 'hrm',
    summary: 'The directory and each person’s full record — minus their pay.',
    icon: Users,
    roles: HR_ROLES,
    body: `**Employees** is the team directory and the home of each person's record.

### How to use it
- Browse the directory; open someone to see their full profile.
- Maintain their **record** — job title, department, manager, employment type,
  hire date, trial-period end (auto-set to 3 months from hire), contact details, photo.
- Work their **reviews, development plan, onboarding, skills and bank accounts**
  from the same place (tabs on the employee).

### Compensation
Salary and payslips are **owned by Finance**; HR manages everything else on the
employee's record.

### Why it's here
One authoritative profile per person that HR, Finance and Payroll all rely on —
so records, reporting lines and pay stay consistent across the whole system.`,
  },
  {
    slug: 'hrm-time-off',
    title: 'Time Off',
    module: 'hrm',
    summary: 'Review and approve the team’s leave requests.',
    icon: Palmtree,
    roles: HR_ROLES,
    body: `**Time Off** (HR side) is where you handle the team's leave. Employees raise
requests from their My Space; you see and action them here.

### How to use it
- Review incoming requests (**pending → approved / declined**).
- Filter by employee or status to plan around coverage.

### Why it's here
A shared, accurate record of who's off and when — no scattered emails, and a
clear approval trail.`,
  },
  {
    slug: 'hrm-onboarding',
    title: 'Onboarding',
    module: 'hrm',
    summary: 'Build the getting-started checklist new hires work through.',
    icon: ListChecks,
    roles: HR_ROLES,
    body: `**Onboarding** lets HR set up each new hire's getting-started checklist.

### How to use it
1. Create a checklist of items for the employee.
2. It starts as **To do**; the employee **Starts** it and ticks items off in their
   My Space (**To do → In progress → Done**).
3. Track their progress from here.

### Why it's here
A consistent, trackable first-weeks experience — nothing important is forgotten,
and you can see at a glance how each new joiner is settling in.`,
  },
  {
    slug: 'hrm-reviews',
    title: 'Performance Reviews',
    module: 'hrm',
    summary: 'Run review cycles and agree action items with each person.',
    icon: Star,
    roles: HR_ROLES,
    body: `**Performance Reviews** is where you run review cycles.

### How to use it
1. Create a review for an employee (period, rating, summary) and **submit** it.
2. The employee **acknowledges** it in their My Space and you both tick off the
   agreed **action items** — handy for walking the list together in the 1:1.

### Why it's here
It turns reviews into a shared, followed-up conversation rather than a form filed
and forgotten — with a clear record of what was agreed.`,
  },
  {
    slug: 'hrm-pdp',
    title: 'Development Plans (PDP)',
    module: 'hrm',
    summary: 'Set growth goals and track them over time.',
    icon: Target,
    roles: HR_ROLES,
    body: `**Development Plans** capture where each person is heading — their growth
goals over time.

### How to use it
- Create a plan with **goals** for an employee.
- The employee moves their own goals along (**open → in progress → done**) from
  My Space; you keep the bigger picture here.

### Why it's here
Career growth needs a plan, not just good intentions. A living PDP keeps
development deliberate and visible to both sides.`,
  },
  {
    slug: 'hrm-skills',
    title: 'Skills Matrix',
    module: 'hrm',
    summary: 'See the team’s skills and levels at a glance.',
    icon: Grid3x3,
    roles: HR_ROLES,
    body: `The **Skills Matrix** is the org-wide view of who has which skills, and at
what level.

### How to use it
- Scan skills across the team to spot **strengths and gaps**.
- Skills come from a shared catalogue; employees add their own (with a 1–5 level)
  from My Space.

### Why it's here
It answers "do we have the skills to take this on — and where should we invest in
growth?" at a glance, instead of guessing.`,
  },
  {
    slug: 'hrm-org-chart',
    title: 'Org Chart',
    module: 'hrm',
    summary: 'The reporting structure of the organisation.',
    icon: Network,
    roles: HR_ROLES,
    body: `The **Org Chart** visualises the team's reporting structure — who reports to
whom, derived from each employee's **manager**.

### How to use it
- Read the structure top-down to understand reporting lines.
- Keep it accurate by setting the **manager** on each employee's record.

### Why it's here
A clear picture of how the organisation is shaped — useful for onboarding,
planning and knowing who owns what.`,
  },
];
